import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Building2, ArrowRight } from 'lucide-react';

interface PendingOrgData {
  userId: string;
  planData: {
    planId: string;
    planName: string;
    customerId: string;
    subscriptionId: string;
  };
  sessionId: string;
}

export default function CreateOrganizationAfterPayment() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [pendingData, setPendingData] = useState<PendingOrgData | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: ''
  });

  useEffect(() => {
    // Recuperar dados pendentes do localStorage
    console.log('Verificando dados pendentes no localStorage...');
    const stored = localStorage.getItem('pendingOrgCreation');
    console.log('Dados encontrados:', stored);
    
    if (!stored) {
      console.log('Nenhum dado pendente encontrado');
      toast({
        title: "Dados não encontrados",
        description: "Redirecionando para página inicial...",
        variant: "destructive"
      });
      setTimeout(() => navigate('/'), 2000);
      return;
    }

    try {
      const data = JSON.parse(stored);
      console.log('Dados parseados:', data);
      setPendingData(data);
    } catch (error) {
      console.error('Erro ao parsear dados pendentes:', error);
      navigate('/');
    }
  }, [navigate, toast]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: generateSlug(value)
    }));
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !pendingData) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, insira o nome da organização.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // Criar organização
      const { data: orgData, error: orgError } = await supabase.functions.invoke('create-organization-with-subscription', {
        body: {
          organizationData: {
            name: formData.name.trim(),
            slug: formData.slug.trim() || generateSlug(formData.name),
            description: formData.description.trim()
          },
          subscriptionData: {
            userId: pendingData.userId,
            stripeCustomerId: pendingData.planData.customerId,
            stripeSubscriptionId: pendingData.planData.subscriptionId,
            planId: pendingData.planData.planId,
            planName: pendingData.planData.planName,
            sessionId: pendingData.sessionId
          }
        }
      });

      if (orgError) throw orgError;

      // Limpar dados pendentes
      localStorage.removeItem('pendingOrgCreation');

      toast({
        title: "Organização criada!",
        description: "Sua organização foi configurada com sucesso. Bem-vindo à plataforma!",
      });

      // Redirecionar para a intranet
      navigate('/intranet');

    } catch (error: any) {
      console.error('Erro ao criar organização:', error);
      toast({
        title: "Erro ao criar organização",
        description: error.message || "Erro interno. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!pendingData) {
    return null; // Será redirecionado no useEffect
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Building2 className="w-16 h-16 text-primary" />
          </div>
          <CardTitle className="text-2xl">Configurar Organização</CardTitle>
          <p className="text-muted-foreground">
            Últimas etapas para começar a usar sua conta do plano <strong>{pendingData.planData.planName}</strong>
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateOrganization} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome da Organização *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Ex: Minha Empresa LTDA"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Identificador único</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="minha-empresa"
              />
              <p className="text-xs text-muted-foreground">Será usado na URL. Somente letras, números e hífens.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição (opcional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Breve descrição da sua organização..."
                rows={3}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
            >
              {loading ? "Criando..." : (
                <>
                  Finalizar Configuração
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              Após criar a organização, você será direcionado para o painel principal da plataforma.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}